import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  Check,
  Copy,
  Eye,
  EyeOff,
  ExternalLink,
  KeyRound,
  Plus,
  Receipt,
  Search,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  createVaultAccount,
  listInsurancePolicies,
  listProperties,
  listVaultAccounts,
  removeVaultAccount,
  updateVaultAccount,
} from "@/services/dataService";

const SECTIONS = [
  { key: "operations", label: "Logins", add: "login" },
  { key: "bills", label: "Bills", add: "bill" },
  { key: "banking", label: "Bank & cards", add: "bank or card" },
  { key: "policies", label: "Policies" },
];
const addLabel = (key) => SECTIONS.find((item) => item.key === key)?.add || "login";

const EMPTY_FORM = {
  name: "",
  propertyId: "",
  loginEmail: "",
  username: "",
  secret: "",
  accountNumber: "",
  url: "",
  autopay: false,
  notes: "",
};

function normalizeUrl(value) {
  const trimmed = (value || "").trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  return `https://${trimmed}`;
}

export default function Vault() {
  const { currentUser } = useAuth();
  const userId = currentUser?._id;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [section, setSection] = useState("operations");
  const [searchTerm, setSearchTerm] = useState("");
  const [revealed, setRevealed] = useState(() => new Set());
  const [copiedKey, setCopiedKey] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const accountsQuery = useQuery({
    queryKey: ["vaultAccounts", userId],
    queryFn: () => listVaultAccounts(),
    enabled: Boolean(userId),
  });

  const policiesQuery = useQuery({
    queryKey: ["portfolio", "insurance", userId],
    queryFn: () => listInsurancePolicies(),
    enabled: Boolean(userId),
  });
  const policies = policiesQuery.data || [];

  const propertiesQuery = useQuery({
    queryKey: ["properties", userId],
    queryFn: () => listProperties(),
    enabled: Boolean(userId),
  });

  const propertyName = useMemo(() => {
    const map = new Map();
    for (const property of propertiesQuery.data || []) {
      map.set(String(property._id), property.name || property.address);
    }
    return map;
  }, [propertiesQuery.data]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["vaultAccounts", userId] });

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      editing ? updateVaultAccount(payload) : createVaultAccount(payload),
    onSuccess: async () => {
      await invalidate();
      closeDialog();
      toast({ title: editing ? "Account updated" : "Account saved" });
    },
    onError: (error) =>
      toast({ title: "Could not save", description: error.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: removeVaultAccount,
    onSuccess: async () => {
      await invalidate();
      toast({ title: "Account deleted" });
    },
    onError: (error) =>
      toast({ title: "Could not delete", description: error.message, variant: "destructive" }),
  });

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (accountsQuery.data || [])
      .filter((account) => account.section === section)
      .filter((account) => {
        if (!term) return true;
        return [account.name, account.loginEmail, account.username, account.accountNumber, account.notes]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(term);
      });
  }, [accountsQuery.data, section, searchTerm]);

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setDialogOpen(true);
  }

  function openEdit(account) {
    setEditing(account);
    setForm({
      name: account.name || "",
      propertyId: account.propertyId ? String(account.propertyId) : "",
      loginEmail: account.loginEmail || "",
      username: account.username || "",
      secret: account.secret || "",
      accountNumber: account.accountNumber || "",
      url: account.url || "",
      autopay: Boolean(account.autopay),
      notes: account.notes || "",
    });
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditing(null);
    setForm({ ...EMPTY_FORM });
  }

  function toggleReveal(id) {
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function copyValue(key, value) {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 1500);
    } catch {
      toast({ title: "Copy failed", description: "Copy manually instead.", variant: "destructive" });
    }
  }

  function handleSave() {
    const base = {
      section,
      name: form.name.trim(),
      loginEmail: form.loginEmail.trim() || undefined,
      username: form.username.trim() || undefined,
      secret: form.secret.trim() || undefined,
      accountNumber: form.accountNumber.trim() || undefined,
      url: form.url.trim() ? normalizeUrl(form.url) : undefined,
      autopay: form.autopay,
      notes: form.notes.trim() || undefined,
    };
    if (editing) {
      saveMutation.mutate({
        id: editing._id,
        ...base,
        propertyId: form.propertyId || undefined,
        clearProperty: !form.propertyId,
      });
    } else {
      saveMutation.mutate({
        ...base,
        propertyId: form.propertyId || undefined,
      });
    }
  }

  function handleDelete(account) {
    if (window.confirm(`Delete "${account.name}"? This can't be undone.`)) {
      deleteMutation.mutate(account._id);
    }
  }

  return (
    <PageLayout
      title="Accounts"
      actions={
        section === "policies" ? null : (
          <Button size="sm" onClick={openCreate} className="gap-1.5">
            <Plus className="h-4 w-4" /> Add {addLabel(section)}
          </Button>
        )
      }
    >
      <div className="space-y-4">
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />
          Only you can see this. Passwords stay hidden until you tap the eye.
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex flex-wrap rounded-lg border border-input bg-background p-0.5">
            {SECTIONS.map((item) => {
              const active = section === item.key;
              const count =
                item.key === "policies"
                  ? policies.length
                  : (accountsQuery.data || []).filter((a) => a.section === item.key).length;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setSection(item.key)}
                  className={`rounded-md px-3 py-1.5 text-sm ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {item.label} <span className="tabular-nums opacity-70">{count}</span>
                </button>
              );
            })}
          </div>
          <label className="relative min-w-[200px] flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search name, email, account #"
              aria-label="Search accounts"
            />
          </label>
        </div>

        {section === "policies" ? (
          <div className="hh-panel overflow-x-auto">
            <table className="hh-table min-w-[720px]">
              <thead>
                <tr>
                  <th>House</th>
                  <th>Insurer</th>
                  <th>Policy #</th>
                  <th>Renews</th>
                  <th className="num">Premium</th>
                  <th>Agent</th>
                </tr>
              </thead>
              <tbody>
                {policies
                  .filter((p) => !searchTerm.trim() || `${p.propertyLabel} ${p.insuranceName} ${p.policyNumber} ${p.agent || ""}`.toLowerCase().includes(searchTerm.trim().toLowerCase()))
                  .map((policy) => (
                    <tr key={policy._id}>
                      <td className="max-w-[220px] truncate">{propertyName.get(String(policy.propertyId)) || policy.propertyLabel}</td>
                      <td>{policy.insuranceName}</td>
                      <td className="tabular-nums">{policy.policyNumber}</td>
                      <td className={`whitespace-nowrap ${policy.termEnd < Date.now() ? "text-rose-700 dark:text-rose-300" : ""}`}>
                        {new Date(policy.termEnd).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </td>
                      <td className="num">{Number(policy.premium || 0).toLocaleString("en-US", { style: "currency", currency: "USD" })}</td>
                      <td className="max-w-[200px] truncate text-muted-foreground">{policy.agent || "—"}</td>
                    </tr>
                  ))}
                {!policies.length ? (
                  <tr>
                    <td colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                      No policies yet. <a href="/insurance" className="underline">Add one on Insurance &amp; licenses</a>.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
            <p className="px-3 py-2 text-xs text-muted-foreground">
              Edit policies on <a href="/insurance" className="underline">Insurance &amp; licenses</a>.
            </p>
          </div>
        ) : (
          <div className="hh-panel overflow-x-auto">
            <table className="hh-table min-w-[820px]">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Login</th>
                  <th>Password</th>
                  <th>Account #</th>
                  <th>Link</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((account) => {
                  const isRevealed = revealed.has(account._id);
                  const login = account.loginEmail || account.username;
                  return (
                    <tr key={account._id} className="group">
                      <td className="max-w-[240px]">
                        <span className="block truncate font-medium">{account.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {[account.propertyId ? propertyName.get(String(account.propertyId)) : "", account.autopay ? "Autopay" : "", account.notes]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </td>
                      <td className="max-w-[220px]">
                        {login ? (
                          <span className="flex items-center gap-1.5">
                            <span className="truncate">{login}</span>
                            <CopyButton
                              copied={copiedKey === `${account._id}:login`}
                              onCopy={() => copyValue(`${account._id}:login`, login)}
                              label="login"
                            />
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td>
                        {account.secret ? (
                          <span className="flex items-center gap-1.5">
                            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                              {isRevealed ? account.secret : "••••••••"}
                            </code>
                            <button
                              type="button"
                              onClick={() => toggleReveal(account._id)}
                              className="text-muted-foreground hover:text-foreground"
                              aria-label={isRevealed ? "Hide password" : "Show password"}
                            >
                              {isRevealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                            <CopyButton
                              copied={copiedKey === `${account._id}:secret`}
                              onCopy={() => copyValue(`${account._id}:secret`, account.secret)}
                              label="password"
                            />
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="tabular-nums">
                        {account.accountNumber ? (
                          <span className="flex items-center gap-1.5">
                            {account.accountNumber}
                            <CopyButton
                              copied={copiedKey === `${account._id}:acct`}
                              onCopy={() => copyValue(`${account._id}:acct`, account.accountNumber)}
                              label="account number"
                            />
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td>
                        {account.url ? (
                          <a
                            href={normalizeUrl(account.url)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                          >
                            Open <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap text-right">
                        <Button variant="ghost" size="sm" className="h-8" onClick={() => openEdit(account)}>
                          Edit
                        </Button>
                        <button
                          type="button"
                          className="ml-1 inline-grid h-8 w-8 place-items-center rounded-md text-muted-foreground opacity-0 hover:bg-muted hover:text-rose-600 focus:opacity-100 group-hover:opacity-100"
                          onClick={() => handleDelete(account)}
                          aria-label={`Delete ${account.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!accountsQuery.isLoading && filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                      Nothing in {SECTIONS.find((s) => s.key === section)?.label} yet.{" "}
                      <button type="button" className="underline" onClick={openCreate}>
                        Add one
                      </button>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={(open) => (open ? setDialogOpen(true) : closeDialog())}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit account" : `Add ${addLabel(section)}`}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-3">
            <div className="space-y-1">
              <Label>Name</Label>
              <Input
                placeholder={section === "bills" ? "Baltimore Water, HOA…" : section === "banking" ? "M&T checking, Chase Ink…" : "Stessa, QuickBooks…"}
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Login email</Label>
                <Input
                  value={form.loginEmail}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, loginEmail: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Username</Label>
                <Input
                  value={form.username}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, username: event.target.value }))
                  }
                />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Password / passcode</Label>
                <Input
                  type="text"
                  placeholder="Optional"
                  value={form.secret}
                  onChange={(event) => setForm((prev) => ({ ...prev, secret: event.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label>Account number</Label>
                <Input
                  value={form.accountNumber}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, accountNumber: event.target.value }))
                  }
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Portal / login link</Label>
              <Input
                placeholder="https://…"
                value={form.url}
                onChange={(event) => setForm((prev) => ({ ...prev, url: event.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Property (optional)</Label>
              <select
                value={form.propertyId}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, propertyId: event.target.value }))
                }
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              >
                <option value="">Not tied to a property</option>
                {(propertiesQuery.data || []).map((property) => (
                  <option key={property._id} value={property._id}>
                    {property.name || property.address}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.autopay}
                onChange={(event) => setForm((prev) => ({ ...prev, autopay: event.target.checked }))}
                className="h-4 w-4 rounded border"
              />
              On autopay
            </label>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input
                value={form.notes}
                onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saveMutation.isPending || !form.name.trim()}>
              {saveMutation.isPending ? "Saving…" : editing ? "Save changes" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}

function CopyButton({ copied, onCopy, label }) {
  return (
    <button
      type="button"
      onClick={onCopy}
      className="shrink-0 text-muted-foreground opacity-60 hover:text-foreground hover:opacity-100"
      aria-label={`Copy ${label}`}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function CopyRow({ label, value, copied, onCopy }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-14 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="truncate">{value}</span>
      <button
        type="button"
        onClick={onCopy}
        className="text-muted-foreground transition hover:text-foreground"
        aria-label={`Copy ${label}`}
      >
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}
